import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewEncapsulation,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Curation image-replacement panel: reproduces the AngularJS ft-image-upload markup and
 * re-initialises Dropzone on the same element (byte-identical options) so the box renders
 * pixel-identically. `window.Dropzone` is the global set at bootstrap. On upload success
 * emits `imageUploaded`; the host owns the replacePhoto work.
 */
@Component({
    selector: 'ft-curation-image-replacement-ng',
    imports: [TranslateModule],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-curation-image-replacement.component.html'
})
export class FtCurationImageReplacementComponent implements AfterViewInit, OnDestroy {
    /** v1 `scope.uploadUrl` (computed in the directive link) — the Dropzone POST target. */
    readonly uploadUrl = input<string>();

    /** v1 `<ft-image-upload on-image-uploaded="replacePhoto(image)">` — emits the upload
     * response; wired in curationModal.html to the AngularJS controller's `replacePhoto`. */
    readonly imageUploaded = output<any>();
    /** v1 `on-image-uploading` (unwired in curationModal.html, kept for fidelity). */
    readonly imageUploading = output<void>();

    /** v1 `scope.uploadFailed` — toggles the error row (mirrors `ng-show`). */
    protected readonly uploadFailed = signal(false);

    private readonly dzRoot = viewChild.required<ElementRef<HTMLElement>>('dzRoot');

    private dz: any;

    private readonly zone = inject(NgZone);

    ngAfterViewInit(): void {
        const Dropzone = (window as any).Dropzone;
        if (!Dropzone || !this.uploadUrl()) return;
        const cmp = this;
        this.dz = new Dropzone(this.dzRoot().nativeElement, {
            url: this.uploadUrl(),
            headers: { 'X-XSRF-TOKEN': cmp.getCookie('XSRF-TOKEN') },
            maxFiles: 1,
            previewsContainer: false,
            init: function (this: any) {
                this.on('addedfile', function () {
                    cmp.zone.run(() => {
                        cmp.imageUploading.emit();
                        cmp.uploadFailed.set(false);
                    });
                });
                this.on('success', function (_file: any, response: any) {
                    cmp.zone.run(() => {
                        cmp.imageUploaded.emit(response);
                    });
                });
                this.on('error', function () {
                    cmp.zone.run(() => {
                        cmp.uploadFailed.set(true);
                        cmp.imageUploaded.emit(undefined);
                    });
                });
                this.on('maxfilesexceeded', function (this: any, file: any) {
                    this.removeAllFiles();
                    this.addFile(file);
                });
            },
        });
    }

    ngOnDestroy(): void {
        if (this.dz && typeof this.dz.destroy === 'function') this.dz.destroy();
    }

    /** Verbatim from imageUpload.js — read the XSRF cookie for the upload POST header. */
    private getCookie(key: string): string | undefined {
        const cookieArray = document.cookie.split('; ');
        const cookies: Record<string, string> = {};
        for (let i = 0; i < cookieArray.length; i++) {
            const cookie = cookieArray[i];
            const index = cookie.indexOf('=');
            if (index > 0) {
                const name = decodeURIComponent(cookie.substring(0, index));
                if (typeof cookies[name] === 'undefined') {
                    cookies[name] = decodeURIComponent(cookie.substring(index + 1));
                }
            }
        }
        return cookies[key];
    }
}
